import { useEffect, useRef, useState } from "react";
import {
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { MoreHorizontal, Send, ShoppingBag, Volume2 } from "lucide-react";
import { JoinStageIcon } from "./icons/JoinStageIcon";
import { LeaveStageIcon } from "./icons/LeaveStageIcon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { MeetingLogoutButton } from "./MeetingLogoutButton";
import { LivestreamChatOverlay } from "./LivestreamChatOverlay";
import { LivestreamShopOverlay } from "./LivestreamShopOverlay";
import { LivestreamVideoGrid } from "./LivestreamVideoGrid";
import {
  isIosDeviceNeedingAudioGesture,
  onLivestreamRemoteAudioUnlocked,
  unlockLivestreamRemoteAudio,
} from "./LivestreamParticipantVideo";
import { useLivestreamChat } from "./useLivestreamChat";
import { useLivestreamStageFeeds } from "./useLivestreamStageFeeds";
import { useLivestreamStageRequest } from "./useLivestreamStageRequest";
import { useMeetingShopProducts } from "./useMeetingShopProducts";
import { MeetingViewerAuthGate } from "./MeetingViewerAuthGate";
import type { MeetingLeadContact } from "./meetingProductTypes";

export function LivestreamViewerView({
  shopProductIds,
  leadContact,
  showLogout,
  loggingOut,
  onLogout,
}: {
  shopProductIds: string[];
  leadContact?: MeetingLeadContact;
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const chatInputRef = useRef<HTMLInputElement>(null);
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const [chatDraft, setChatDraft] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [showAudioUnlockOverlay, setShowAudioUnlockOverlay] = useState(
    () => isIosDeviceNeedingAudioGesture(),
  );

  useEffect(() => {
    if (!showAudioUnlockOverlay) return;
    return onLivestreamRemoteAudioUnlocked(() => {
      setShowAudioUnlockOverlay(false);
    });
  }, [showAudioUnlockOverlay]);
  const showLogoutButton = showLogout && roomState !== "joined";
  const hasShopProducts = shopProductIds.length > 0;
  const { products: shopProducts, loading: loadingShopProducts, load: loadShopProducts } =
    useMeetingShopProducts({ productIds: shopProductIds });
  const { messages, sendMessage } = useLivestreamChat({ meeting });
  const stageFeeds = useLivestreamStageFeeds({ meeting, includeSelf: false });
  const {
    requesting: requestingJoin,
    cancelling: cancellingJoinRequest,
    leaving: leavingStage,
    requestJoin,
    cancelRequestJoin,
    leaveStage,
    canRequestJoin,
    canCancelRequestJoin,
    canLeaveStage,
    requestJoinLabel,
    cancelRequestJoinLabel,
  } = useLivestreamStageRequest({ meeting });
  const hasStageScreenShare = stageFeeds.some(
    (participant) =>
      participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video),
  );
  const hasChatDraft = Boolean(chatDraft.trim());
  const showLeaveStage = !hasChatDraft && canLeaveStage;
  const showCancelRequestJoin = !hasChatDraft && canCancelRequestJoin;
  const showRequestJoin = !hasChatDraft && !hasStageScreenShare && canRequestJoin;
  const showMoreMenu = hasShopProducts || showRequestJoin;

  const handleOpenShop = () => {
    setShopOpen(true);
    void loadShopProducts();
  };

  const handleSendChat = async () => {
    if (!chatDraft.trim() || sendingChat) return;
    setSendingChat(true);
    try {
      await sendMessage({ text: chatDraft });
      setChatDraft("");
    } finally {
      setSendingChat(false);
      chatInputRef.current?.focus();
    }
  };

  if (!meeting) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <MeetingViewerAuthGate>
    <div className="relative h-dvh w-screen overflow-hidden bg-black">
      {showLogoutButton ? (
        <MeetingLogoutButton
          showLogout={showLogoutButton}
          loggingOut={loggingOut}
          onLogout={onLogout}
        />
      ) : null}
      <LivestreamVideoGrid
        participants={stageFeeds}
        selfId={selfId}
        waitingLabel="Waiting for host…"
        hostPipDraggable
      />
      {showAudioUnlockOverlay ? (
        <div className="fixed inset-0 z-[110] flex flex-col items-center justify-center gap-6 bg-black px-6 text-center">
          <p className="max-w-sm text-lg font-medium text-white">
            Tap below to hear the livestream
          </p>
          <p className="max-w-sm text-sm text-white/70">
            iOS requires a tap before audio can play. Video will continue in the background.
          </p>
          <Button
            type="button"
            size="lg"
            className="h-12 gap-2 px-8 text-base"
            onClick={() => {
              unlockLivestreamRemoteAudio();
              setShowAudioUnlockOverlay(false);
            }}
          >
            <Volume2 className="h-5 w-5" aria-hidden />
            Enable audio
          </Button>
        </div>
      ) : null}
      <LivestreamChatOverlay messages={messages} />
      <LivestreamShopOverlay
        open={shopOpen}
        onClose={() => setShopOpen(false)}
        products={shopProducts}
        loading={loadingShopProducts}
        leadContact={leadContact}
      />
      <form
        className="absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-3 right-3 z-50 flex items-center gap-2 sm:left-4 sm:right-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (hasChatDraft) {
            void handleSendChat();
          }
        }}
      >
        <Input
          ref={chatInputRef}
          value={chatDraft}
          onChange={(event) => setChatDraft(event.target.value)}
          placeholder="Say something…"
          disabled={sendingChat}
          className="h-10 flex-1 border-white/20 bg-black/55 text-white placeholder:text-white/50 backdrop-blur-sm"
          aria-label="Live chat message"
        />
        {hasChatDraft ? (
          <Button
            type="submit"
            size="icon"
            variant="outline"
            disabled={sendingChat}
            className="h-10 w-10 shrink-0 border-white/20 bg-black/55 text-white hover:bg-white/10 hover:text-white"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        ) : showLeaveStage ? (
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={leavingStage}
            onClick={() => {
              if (leavingStage) return;
              void leaveStage();
            }}
            className="h-10 w-10 shrink-0 border-white/20 bg-black/55 text-white hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-50"
            aria-label="Leave stage"
          >
            <LeaveStageIcon className="h-full! w-full! p-1 text-red-500" />
          </Button>
        ) : showCancelRequestJoin ? (
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={cancellingJoinRequest}
            onClick={() => {
              if (cancellingJoinRequest) return;
              void cancelRequestJoin();
            }}
            className="h-10 w-10 shrink-0 border-white/20 bg-black/55 text-white hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-50"
            aria-label={cancelRequestJoinLabel}
          >
            <LeaveStageIcon className="h-full! w-full! p-1 text-red-500" />
          </Button>
        ) : null}
        {showMoreMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-10 w-10 shrink-0 border-white/20 bg-black/55 text-white hover:bg-white/10 hover:text-white"
                aria-label="More options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="min-w-44">
              {showRequestJoin ? (
                <DropdownMenuItem
                  disabled={requestingJoin}
                  onClick={() => {
                    if (requestingJoin) return;
                    void requestJoin();
                  }}
                >
                  <JoinStageIcon className="h-4 w-4" />
                  {requestJoinLabel}
                </DropdownMenuItem>
              ) : null}
              {hasShopProducts ? (
                <DropdownMenuItem onClick={handleOpenShop}>
                  <ShoppingBag className="h-4 w-4" />
                  Shop
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </form>
    </div>
    </MeetingViewerAuthGate>
  );
}
